/* THE VALUE LADDER (docs/ART.md, 2026-09-27). Every country's palette is pixel art's: a tree colour nearly black, a
 * sand as dark as the grass. The stylised world takes the HUE from the country and the VALUE from the role, so a heath,
 * a dust bowl and an ash country read as one game: sand lightest, then rock, grass, crowns, trunks. In OKLab. */

type RGB = [number, number, number];

const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const gam = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function toLab([r, g, b]: RGB): RGB {
  const R = lin(r), G = lin(g), B = lin(b);
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}
export function fromLab([L, a, b]: RGB): RGB {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3, m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3, s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const c = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
  return c.map((v) => Math.max(0, Math.min(1, gam(Math.max(0, v))))) as RGB;
}
export const hexRgb = (h: string): RGB => { const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(h || ''); return m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : [0.5, 0.5, 0.5]; };

/** The roles and their bands: [L lo, L hi, chroma cap, warm pull]. */
export const LADDER = {
  sand: [0.84, 0.88, 0.06, 0.25],
  rock: [0.74, 0.80, 0.03, 0.0],
  ground: [0.71, 0.77, 0.11, 0.12],
  tip: [0.80, 0.84, 0.12, 0.2],
  crown: [0.62, 0.68, 0.10, 0.08],
  trunk: [0.40, 0.46, 0.05, 0.2],
  scorch: [0.46, 0.50, 0.03, 0.0],
  ice: [0.86, 0.90, 0.04, -0.2],
} as const;
export type Role = keyof typeof LADDER;

const WARM_H = Math.atan2(0.09, 0.05);   // the hue the ladder leans to: a yellow ochre

/** A country colour moved into its role's band: lightness clamped into [lo, hi] (a colour inside keeps its own), chroma
 *  capped, the hue pulled a little toward ochre. */
export function role(hex: string | RGB, r: Role): RGB {
  const [lo, hi, cap, warm] = LADDER[r];
  const [L, a, b] = toLab(typeof hex === 'string' ? hexRgb(hex) : hex);
  let C = Math.hypot(a, b), h = Math.atan2(b, a);
  if (C < 1e-4) h = WARM_H;
  let dh = WARM_H - h; while (dh > Math.PI) dh -= 2 * Math.PI; while (dh < -Math.PI) dh += 2 * Math.PI;
  h += dh * warm; C = Math.min(C, cap);
  const L2 = Math.max(lo, Math.min(hi, L));
  return fromLab([L2, C * Math.cos(h), C * Math.sin(h)]);
}

/** THE FIELD GUIDE'S PALETTE (docs/ART.md 4, 2026-09-28): every role has its own watercolour, and the country only leans
 *  it -- a quarter of the way toward its own colour, moved onto the role's ladder band -- so a heath and an ash country
 *  are the same book with a different season's wash. */
const BOOK: Record<string, [string, Role]> = {
  ground: ['#bcc68e', 'ground'], shade: ['#adb97e', 'ground'], tip: ['#6d7a45', 'ground'], sand: ['#e8d6ab', 'sand'],
  rock: ['#cfcbc0', 'rock'], crown: ['#a3b776', 'crown'], trunk: ['#8a6a4a', 'trunk'], scorch: ['#6a625a', 'scorch'], ice: ['#d8e6ea', 'ice'],
};
const LEAN = 0.25;
const cache = new WeakMap<object, Record<string, RGB>>();
export function looksOf(pal: any): Record<string, RGB> {
  let o = cache.get(pal); if (o) return o;
  const src: Record<string, string | undefined> = { ground: pal.ground, shade: pal.shade || pal.ground, tip: undefined, sand: pal.sand, rock: undefined, crown: pal.tree2 || pal.tree, trunk: pal.trunk, scorch: undefined, ice: undefined };
  o = {};
  for (const k of Object.keys(BOOK)) { const [hex, r] = BOOK[k]; const base = hexRgb(hex); const c = src[k]; if (!c) { o[k] = base; continue; }
    const lean = role(c, r); o[k] = [0, 1, 2].map((i) => base[i] + (lean[i] - base[i]) * LEAN) as RGB; }
  cache.set(pal, o); return o;
}

/** One object's own step off its role's colour: a lightness step and a small turn of hue -- how neighbouring trees differ
 *  (docs/ART.md 4: by lightness and a small hue step, never a random colour). */
export function vary(c: RGB, dL: number, dh: number): RGB {
  const [L, a, b] = toLab(c); const C = Math.hypot(a, b), h = Math.atan2(b, a) + dh;
  return fromLab([L + dL, C * Math.cos(h), C * Math.sin(h)]);
}

/** A creature's colour as a wash (docs/ART.md 3): the pixel palette's dark hides lifted into a watercolour's range -- the
 *  hue kept, the lightness raised and compressed so nothing living reads as a dark lump on the paper. */
const washCache = new Map<string, string>();
export function washOf(hex: string): string {
  const had = washCache.get(hex); if (had) return had;
  const [L, a, b] = toLab(hexRgb(hex)); const L2 = 0.3 + L * 0.52, k = 1.2;   /* the shader lifts a body again (pow 0.66): this only brings the darkest hides up to where that lift can reach */
  const c = fromLab([L2, a * k, b * k]); const out = '#' + c.map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
  washCache.set(hex, out); return out;
}
